import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormLabel from '@mui/material/FormLabel'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Slider from '@mui/material/Slider'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'

import Langs, { Localize } from '../../../app/lang/langs'
import { showErrorToast, showSuccessToast } from '../../../app/utils/helpers'
import {
    deleteVLearnQuestion,
    listVLearnQuestions,
    listVLearnQuizzes,
    reorderVLearnQuestions,
    saveVLearnQuiz, deleteVLearnQuiz
} from '../../../containers/vlearn/vlearnSlice'
import QuestionEditorDialog from './QuestionEditorDialog'
import QuizImportDialog from './QuizImportDialog'
import { vlearnSurface, vlearnType, VLearnEmptyState } from '../vlearnUi'
import ConfirmDialog from '../../popups/ConfirmDialog'

/** The enum `saveQuiz` accepts. Any other value is a 422, so the Select is the whole domain. */
const SHOW_ANSWERS_OPTIONS = [
    { value: 'never', labelKey: 'VLEARN_EDITOR_ANSWERS_NEVER', helpKey: 'VLEARN_EDITOR_ANSWERS_NEVER_HELP' },
    { value: 'after_submit', labelKey: 'VLEARN_EDITOR_ANSWERS_AFTER_SUBMIT', helpKey: 'VLEARN_EDITOR_ANSWERS_AFTER_SUBMIT_HELP' },
    { value: 'after_pass', labelKey: 'VLEARN_EDITOR_ANSWERS_AFTER_PASS', helpKey: 'VLEARN_EDITOR_ANSWERS_AFTER_PASS_HELP' },
    { value: 'after_last_attempt', labelKey: 'VLEARN_EDITOR_ANSWERS_AFTER_LAST', helpKey: 'VLEARN_EDITOR_ANSWERS_AFTER_LAST_HELP' }
]

/**
 * V-Learning — authoring a quiz.
 *
 * The settings panel mirrors `saveQuiz`'s body one-for-one, which is the point: every
 * control here writes a column the grader actually reads, and there is no setting in the
 * form that the backend ignores. That is what V-Sécurité's `allow_scores` was — a switch
 * the server never honoured — and an author who cannot trust the form cannot author.
 *
 * Every helper text under a control says what the setting DOES rather than restating its
 * name. `show_answers` and `partial_credit` in particular change what a learner is allowed
 * to see and how a half-right answer scores; an author picking either of those blind is an
 * author picking wrong.
 *
 * @param {object} props
 * @param {number} props.idCourse
 * @param {number} props.idQuiz
 * @param {Function} props.onBack
 */
export default function QuizEditor({ idCourse, idQuiz, onBack }) {
    const dispatch = useDispatch()
    const lang = useSelector((state) => state.settings.lang)

    const quizzes = useSelector((state) => state.vlearn.quizzes)
    const storedQuestions = useSelector((state) => state.vlearn.quizQuestions)

    /**
     * The question list comes from the store, which `listVLearnQuestions` fills.
     *
     * There was briefly a local mirror here, because `listQuestions` answered under
     * `questions` while every other V-Learning list endpoint — and therefore every reducer —
     * uses `list`. The controller was corrected rather than the symptom papered over, so the
     * store is now the single source and the mirror is gone.
     */
    // Memoised on the store value: a bare `: []` literal is a new array every render, which
    // would change the identity of every useMemo/useCallback that depends on it.
    const questions = useMemo(
        () => (Array.isArray(storedQuestions) ? storedQuestions : []), [storedQuestions])

    const [editingQuestion, setEditingQuestion] = useState(null)
    const [questionDialogOpen, setQuestionDialogOpen] = useState(false)
    const [importOpen, setImportOpen] = useState(false)
    const [pendingDelete, setPendingDelete] = useState(null)
    const [quizDeleteOpen, setQuizDeleteOpen] = useState(false)
    const [saving, setSaving] = useState(false)

    const [form, setForm] = useState({
        title: '',
        description: '',
        passing_percent: 70,
        max_attempts: 0,
        time_limit_minutes: '',
        shuffle_questions: false,
        shuffle_options: false,
        partial_credit: false,
        show_score: true,
        show_answers: 'after_submit',
        is_final: false
    })

    const quiz = useMemo(
        () => (Array.isArray(quizzes) ? quizzes.find((row) => Number(row.id) === Number(idQuiz)) : null) || null,
        [quizzes, idQuiz])

    const loadQuestions = useCallback(async () => {
        // The reducer writes the result into state.vlearn.quizQuestions; nothing needs to be
        // read off the response here.
        await dispatch(listVLearnQuestions({ id_course: idCourse, id_quiz: idQuiz }))
    }, [dispatch, idCourse, idQuiz])

    useEffect(() => {
        if (!idCourse || !idQuiz) return
        dispatch(listVLearnQuizzes({ id_course: idCourse }))
        loadQuestions()
    }, [dispatch, idCourse, idQuiz, loadQuestions])

    // Hydrated from the row rather than kept in sync with it: the form is the author's
    // working copy, and re-seeding it on every list refresh would discard their edits.
    //
    // Which is what the dependency on `quiz` used to do anyway. `listVLearnQuizzes` runs on
    // mount and again after every save, handing back a new object each time, so this effect
    // re-ran and overwrote whatever had been typed in the meantime — a title half entered
    // while a refresh was in flight simply vanished. Seeding is keyed on the quiz's identity
    // instead: a different quiz re-seeds, the same quiz arriving again does not.
    const seededQuizIdRef = useRef(null)
    useEffect(() => {
        if (!quiz) return
        if (seededQuizIdRef.current === quiz.id) return
        seededQuizIdRef.current = quiz.id
        setForm({
            title: quiz.title || '',
            description: quiz.description || '',
            passing_percent: Number(quiz.passing_percent) || 70,
            max_attempts: Number(quiz.max_attempts) || 0,
            time_limit_minutes: quiz.time_limit_minutes ? String(quiz.time_limit_minutes) : '',
            shuffle_questions: !!Number(quiz.shuffle_questions),
            shuffle_options: !!Number(quiz.shuffle_options),
            partial_credit: !!Number(quiz.partial_credit),
            show_score: !!Number(quiz.show_score),
            show_answers: quiz.show_answers || 'after_submit',
            is_final: !!Number(quiz.is_final)
        })
    }, [quiz])

    const setField = useCallback((key, value) => {
        setForm((previous) => ({ ...previous, [key]: value }))
    }, [])

    const activeQuestions = useMemo(
        () => questions.filter((question) => question.active === undefined || !!Number(question.active)),
        [questions])

    const totalPoints = useMemo(
        () => activeQuestions.reduce((sum, question) => sum + (Number(question.points) || 0), 0),
        [activeQuestions])

    // ── Settings ────────────────────────────────────────────────────────────────────

    const handleSaveSettings = useCallback(async () => {
        if (!form.title.trim()) {
            showErrorToast(Localize('VLEARN_EDITOR_TOAST_TITLE_REQUIRED', lang))
            return
        }
        setSaving(true)

        const minutes = parseInt(form.time_limit_minutes, 10)
        const action = await dispatch(saveVLearnQuiz({
            id_course: idCourse,
            id_quiz: idQuiz,
            title: form.title.trim(),
            description: form.description.trim() ? form.description.trim() : null,
            passing_percent: form.passing_percent,
            max_attempts: form.max_attempts,
            // Blank means untimed, and the backend reads null as exactly that. Sending 0
            // or an empty string instead would land on its "not an integer" branch — the
            // same outcome, but by accident rather than by contract.
            time_limit_minutes: Number.isInteger(minutes) && minutes >= 1 ? minutes : null,
            shuffle_questions: form.shuffle_questions,
            shuffle_options: form.shuffle_options,
            partial_credit: form.partial_credit,
            show_score: form.show_score,
            show_answers: form.show_answers,
            is_final: form.is_final
        }))
        setSaving(false)

        const data = action && action.payload && action.payload.responseData
        if (!data || data.status !== 1) {
            const message = action && action.payload && action.payload.responseError
                && action.payload.responseError.data && action.payload.responseError.data.error
            showErrorToast(message || Localize('VLEARN_EDITOR_TOAST_SAVE_FAILED', lang))
            return
        }
        showSuccessToast(Localize('VLEARN_EDITOR_TOAST_SAVED', lang))
        dispatch(listVLearnQuizzes({ id_course: idCourse }))
    }, [dispatch, form, idCourse, idQuiz, lang])

    // ── Questions ───────────────────────────────────────────────────────────────────

    const move = useCallback(async (index, direction) => {
        const target = index + direction
        if (target < 0 || target >= activeQuestions.length) return

        const ids = activeQuestions.map((question) => question.id)
        const [moved] = ids.splice(index, 1)
        ids.splice(target, 0, moved)

        // The whole order goes in one request. `reorderQuestions` verifies every id belongs
        // to the quiz and writes a single CASE statement, so there is no half-applied order
        // to recover from — which is why the list is refetched rather than patched locally.
        const action = await dispatch(reorderVLearnQuestions({ id_course: idCourse, id_quiz: idQuiz, ids }))
        const data = action && action.payload && action.payload.responseData
        if (!data || data.status !== 1) {
            showErrorToast(Localize('VLEARN_EDITOR_TOAST_REORDER_FAILED', lang))
            return
        }
        loadQuestions()
    }, [activeQuestions, dispatch, idCourse, idQuiz, lang, loadQuestions])

    const confirmDelete = useCallback(async () => {
        if (!pendingDelete) return
        const action = await dispatch(deleteVLearnQuestion({
            id_course: idCourse, id_question: pendingDelete.id
        }))
        setPendingDelete(null)

        const data = action && action.payload && action.payload.responseData
        if (!data || data.status !== 1) {
            showErrorToast(Localize('VLEARN_EDITOR_TOAST_DELETE_FAILED', lang))
            return
        }
        // `deactivated: true` means the question was answered in a real attempt and had to
        // survive so those attempts still review. Saying so is better than a generic
        // "deleted" that the author will contradict by seeing the row again.
        showSuccessToast(Localize(data.deactivated
            ? 'VLEARN_EDITOR_TOAST_QUESTION_RETIRED'
            : 'VLEARN_EDITOR_TOAST_QUESTION_DELETED', lang))
        loadQuestions()
    }, [dispatch, idCourse, lang, loadQuestions, pendingDelete])

    const confirmDeleteQuiz = useCallback(async () => {
        setQuizDeleteOpen(false)
        const action = await dispatch(deleteVLearnQuiz({ id_course: idCourse, id_quiz: idQuiz }))
        const payload = action && action.payload
        const data = payload && payload.responseData

        if (data && data.status === 1) {
            showSuccessToast(Localize('VLEARN_EDITOR_TOAST_QUIZ_DELETED', lang))
            // The quiz this screen edits no longer exists, so staying here would render an
            // editor for a deleted row.
            if (onBack) onBack()
            return
        }

        // The endpoint refuses for two specific, actionable reasons — a lesson still points at
        // this quiz, or a learner has already attempted it — and names the lesson when it can.
        // axios rejects a 422, so that message arrives on `responseError`, not `responseData`.
        // Surfacing it beats a generic failure: the author needs to know WHICH lesson to change.
        const serverMessage = payload && payload.responseError
            && payload.responseError.data && payload.responseError.data.error
        showErrorToast(serverMessage || Localize('VLEARN_EDITOR_TOAST_QUIZ_DELETE_FAILED', lang))
    }, [dispatch, idCourse, idQuiz, lang, onBack])

    const openQuestion = useCallback((question) => {
        setEditingQuestion(question || null)
        setQuestionDialogOpen(true)
    }, [])

    const handleQuestionSaved = useCallback(() => {
        setQuestionDialogOpen(false)
        setEditingQuestion(null)
        loadQuestions()
    }, [loadQuestions])

    const selectedAnswersOption = SHOW_ANSWERS_OPTIONS.find((option) => option.value === form.show_answers)

    return (
        <Box component="section" aria-labelledby="vlearn-editor-title" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button variant="text" onClick={onBack} startIcon={<ArrowBackRoundedIcon />} sx={quietButtonSx}>
                    <Langs str="VLEARN_EDITOR_BTN_BACK" />
                </Button>
                <Typography id="vlearn-editor-title" component="h2" sx={{ ...vlearnType.pageTitle, flex: 1 }}>
                    <Langs str="VLEARN_EDITOR_TITLE" />
                </Typography>
                <Button
                    variant="text"
                    onClick={() => setQuizDeleteOpen(true)}
                    startIcon={<DeleteOutlineIcon />}
                    sx={{ ...quietButtonSx, color: 'var(--red, rgb(251,105,98))' }}
                >
                    <Langs str="VLEARN_EDITOR_BTN_DELETE_QUIZ" />
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={saving}
                    onClick={handleSaveSettings}
                    startIcon={<SaveRoundedIcon />}
                    sx={primaryButtonSx}
                >
                    <Langs str={saving ? 'VLEARN_EDITOR_BTN_SAVING' : 'VLEARN_EDITOR_BTN_SAVE'} />
                </Button>
            </Stack>

            {/* ── Settings ───────────────────────────────────────────────────────── */}
            <Box sx={{ ...vlearnSurface.card, padding: { xs: '20px', sm: '26px 28px' } }}>
                <Typography component="h3" sx={{ ...vlearnType.sectionTitle, mb: 2.5 }}>
                    <Langs str="VLEARN_EDITOR_SECTION_SETTINGS" />
                </Typography>

                <Stack spacing={3}>
                    <TextField
                        label={Localize('VLEARN_EDITOR_FIELD_TITLE', lang)}
                        value={form.title}
                        onChange={(event) => setField('title', event.target.value)}
                        fullWidth
                        required
                        inputProps={{ maxLength: 190 }}
                    />

                    <TextField
                        label={Localize('VLEARN_EDITOR_FIELD_DESCRIPTION', lang)}
                        value={form.description}
                        onChange={(event) => setField('description', event.target.value)}
                        helperText={<Langs str="VLEARN_EDITOR_FIELD_DESCRIPTION_HELP" />}
                        fullWidth
                        multiline
                        rows={3}
                    />

                    <FormControl component="div" sx={{ width: '100%' }}>
                        <FormLabel component="div" sx={labelSx} id="vlearn-editor-pass-label">
                            <Langs str="VLEARN_EDITOR_FIELD_PASSING" />
                        </FormLabel>
                        <Stack direction="row" spacing={2.5} alignItems="center">
                            <Slider
                                value={form.passing_percent}
                                onChange={(event, value) => setField('passing_percent', value)}
                                min={1}
                                max={100}
                                step={1}
                                valueLabelDisplay="auto"
                                aria-labelledby="vlearn-editor-pass-label"
                                sx={{
                                    color: 'var(--orange, rgb(255,144,21))',
                                    '& .MuiSlider-thumb:focus-visible': {
                                        boxShadow: '0 0 0 8px rgba(255,144,21,0.28)'
                                    }
                                }}
                            />
                            <Chip
                                label={`${form.passing_percent}%`}
                                sx={{ minWidth: 68, fontWeight: 700, borderRadius: '9px' }}
                            />
                        </Stack>
                        <FormHelperText sx={helperSx}>
                            <Langs str="VLEARN_EDITOR_FIELD_PASSING_HELP" />
                        </FormHelperText>
                    </FormControl>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                        <TextField
                            label={Localize('VLEARN_EDITOR_FIELD_MAX_ATTEMPTS', lang)}
                            type="number"
                            value={form.max_attempts}
                            onChange={(event) => {
                                const parsed = parseInt(event.target.value, 10)
                                setField('max_attempts', Number.isInteger(parsed) && parsed > 0 ? parsed : 0)
                            }}
                            helperText={<Langs str="VLEARN_EDITOR_FIELD_MAX_ATTEMPTS_HELP" />}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label={Localize('VLEARN_EDITOR_FIELD_TIME_LIMIT', lang)}
                            type="number"
                            value={form.time_limit_minutes}
                            onChange={(event) => setField('time_limit_minutes', event.target.value)}
                            helperText={<Langs str="VLEARN_EDITOR_FIELD_TIME_LIMIT_HELP" />}
                            inputProps={{ min: 1, step: 1 }}
                            sx={{ flex: 1 }}
                        />
                    </Stack>

                    <Divider />

                    <FormControl fullWidth>
                        <InputLabel id="vlearn-editor-answers-label">
                            <Langs str="VLEARN_EDITOR_FIELD_SHOW_ANSWERS" />
                        </InputLabel>
                        <Select
                            labelId="vlearn-editor-answers-label"
                            label={Localize('VLEARN_EDITOR_FIELD_SHOW_ANSWERS', lang)}
                            value={form.show_answers}
                            onChange={(event) => setField('show_answers', event.target.value)}
                        >
                            {SHOW_ANSWERS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <Box>
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                            <Langs str={option.labelKey} />
                                        </Typography>
                                        <Typography sx={{ ...vlearnType.meta, whiteSpace: 'normal' }}>
                                            <Langs str={option.helpKey} />
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                        {/* The chosen policy is restated below the closed Select as well: the
                            per-option explanations vanish with the menu, and this is the one
                            setting whose wrong value silently leaks the answer key between
                            attempts. */}
                        <FormHelperText sx={helperSx}>
                            {selectedAnswersOption ? <Langs str={selectedAnswersOption.helpKey} /> : null}
                        </FormHelperText>
                    </FormControl>

                    <Stack spacing={1.5}>
                        <SettingSwitch
                            checked={form.show_score}
                            onChange={(value) => setField('show_score', value)}
                            labelKey="VLEARN_EDITOR_FIELD_SHOW_SCORE"
                            helpKey="VLEARN_EDITOR_FIELD_SHOW_SCORE_HELP"
                        />
                        <SettingSwitch
                            checked={form.partial_credit}
                            onChange={(value) => setField('partial_credit', value)}
                            labelKey="VLEARN_EDITOR_FIELD_PARTIAL_CREDIT"
                            /* The formula, not a euphemism. `max(0, correct − incorrect) /
                               totalCorrect` of the question's points means ticking every box
                               scores zero, and an author who does not know that will write a
                               multiple-choice question they think is generous. */
                            helpKey={form.partial_credit
                                ? 'VLEARN_EDITOR_FIELD_PARTIAL_CREDIT_ON_HELP'
                                : 'VLEARN_EDITOR_FIELD_PARTIAL_CREDIT_OFF_HELP'}
                        />
                        <SettingSwitch
                            checked={form.shuffle_questions}
                            onChange={(value) => setField('shuffle_questions', value)}
                            labelKey="VLEARN_EDITOR_FIELD_SHUFFLE_QUESTIONS"
                            helpKey="VLEARN_EDITOR_FIELD_SHUFFLE_QUESTIONS_HELP"
                        />
                        <SettingSwitch
                            checked={form.shuffle_options}
                            onChange={(value) => setField('shuffle_options', value)}
                            labelKey="VLEARN_EDITOR_FIELD_SHUFFLE_OPTIONS"
                            helpKey="VLEARN_EDITOR_FIELD_SHUFFLE_OPTIONS_HELP"
                        />
                        <SettingSwitch
                            checked={form.is_final}
                            onChange={(value) => setField('is_final', value)}
                            labelKey="VLEARN_EDITOR_FIELD_IS_FINAL"
                            helpKey="VLEARN_EDITOR_FIELD_IS_FINAL_HELP"
                        />
                    </Stack>
                </Stack>
            </Box>

            {/* ── Questions ──────────────────────────────────────────────────────── */}
            <Box sx={{ ...vlearnSurface.card }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{ padding: { xs: '18px 18px 12px', sm: '20px 24px 14px' } }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Typography component="h3" sx={{ ...vlearnType.sectionTitle }}>
                            <Langs str="VLEARN_EDITOR_SECTION_QUESTIONS" />
                        </Typography>
                        <Typography sx={{ ...vlearnType.meta }}>
                            {/* Questions and points are counted separately: they pluralise
                                independently, and one string carrying both produced
                                "1 question · 1 points in total". */}
                            <Langs
                                str={activeQuestions.length === 1
                                    ? 'VLEARN_EDITOR_QUESTION_COUNT_ONE'
                                    : 'VLEARN_EDITOR_QUESTION_COUNT_MANY'}
                                params={{ n: activeQuestions.length }}
                            />
                            {Number(totalPoints) > 0 ? (
                                <>
                                    {' · '}
                                    <Langs
                                        str={Number(totalPoints) === 1
                                            ? 'VLEARN_POINTS_ONE'
                                            : 'VLEARN_POINTS_MANY'}
                                        params={{ points: totalPoints }}
                                    />
                                </>
                            ) : null}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        onClick={() => setImportOpen(true)}
                        startIcon={<UploadFileRoundedIcon />}
                        sx={outlinedButtonSx}
                    >
                        <Langs str="VLEARN_EDITOR_BTN_IMPORT" />
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => openQuestion(null)}
                        startIcon={<AddCircleOutlineIcon />}
                        sx={primaryButtonSx}
                    >
                        <Langs str="VLEARN_EDITOR_BTN_ADD_QUESTION" />
                    </Button>
                </Stack>

                <Divider />

                {activeQuestions.length ? (
                    <Stack divider={<Divider />}>
                        {activeQuestions.map((question, index) => (
                            <Stack
                                key={question.id}
                                direction="row"
                                spacing={1.5}
                                alignItems="flex-start"
                                sx={{ padding: { xs: '14px 16px', sm: '16px 24px' } }}
                            >
                                <Chip
                                    label={index + 1}
                                    size="small"
                                    sx={{
                                        minWidth: 30, height: 26, borderRadius: '8px', flexShrink: 0,
                                        fontWeight: 700, fontSize: '0.74rem',
                                        backgroundColor: 'rgba(0,0,0,0.05)', color: 'var(--text-secondary, #4b5263)'
                                    }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ ...vlearnType.cardTitle }}>
                                        {question.question_text}
                                    </Typography>
                                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                                        <Typography sx={{ ...vlearnType.meta }}>
                                            <Langs str={questionTypeKey(question.type)} />
                                        </Typography>
                                        <Typography sx={{ ...vlearnType.meta }}>
                                            {`· ${Number(question.points) === 1
                                                ? Localize('VLEARN_EDITOR_POINT_ONE', lang)
                                                : Localize('VLEARN_EDITOR_POINT_MANY', lang).replace('%n%', Number(question.points) || 1)}`}
                                        </Typography>
                                        <Typography sx={{ ...vlearnType.meta }}>
                                            {`· ${Localize('VLEARN_EDITOR_OPTION_COUNT', lang)
                                                .replace('%n%', (question.options || []).filter((o) => o.active === undefined || !!Number(o.active)).length)}`}
                                        </Typography>
                                        {Number(question.answered_count) > 0 ? (
                                            <Typography sx={{ ...vlearnType.meta, color: '#B25E00', fontWeight: 600 }}>
                                                {`· ${Localize('VLEARN_EDITOR_ANSWERED_COUNT', lang)
                                                    .replace('%n%', Number(question.answered_count))}`}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                </Box>

                                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                                    <Tooltip title={Localize('VLEARN_EDITOR_TOOLTIP_MOVE_UP', lang)}>
                                        <IconButton
                                            size="small"
                                            disabled={index === 0}
                                            onClick={() => move(index, -1)}
                                            aria-label={Localize('VLEARN_EDITOR_TOOLTIP_MOVE_UP', lang)}
                                            sx={iconButtonSx}
                                        >
                                            <ArrowUpwardRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={Localize('VLEARN_EDITOR_TOOLTIP_MOVE_DOWN', lang)}>
                                        <IconButton
                                            size="small"
                                            disabled={index === activeQuestions.length - 1}
                                            onClick={() => move(index, 1)}
                                            aria-label={Localize('VLEARN_EDITOR_TOOLTIP_MOVE_DOWN', lang)}
                                            sx={iconButtonSx}
                                        >
                                            <ArrowDownwardRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={Localize('VLEARN_EDITOR_TOOLTIP_EDIT', lang)}>
                                        <IconButton
                                            size="small"
                                            onClick={() => openQuestion(question)}
                                            aria-label={Localize('VLEARN_EDITOR_TOOLTIP_EDIT', lang)}
                                            sx={iconButtonSx}
                                        >
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={Localize('VLEARN_EDITOR_TOOLTIP_DELETE', lang)}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setPendingDelete(question)}
                                            aria-label={Localize('VLEARN_EDITOR_TOOLTIP_DELETE', lang)}
                                            sx={{ ...iconButtonSx, color: 'var(--red, rgb(251,105,98))' }}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                ) : (
                    <VLearnEmptyState
                        icon={<QuizOutlinedIcon />}
                        title={<Langs str="VLEARN_EDITOR_EMPTY_TITLE" />}
                        description={<Langs str="VLEARN_EDITOR_EMPTY_BODY" />}
                        action={(
                            <Button
                                variant="contained"
                                disableElevation
                                onClick={() => openQuestion(null)}
                                startIcon={<AddCircleOutlineIcon />}
                                sx={primaryButtonSx}
                            >
                                <Langs str="VLEARN_EDITOR_BTN_ADD_QUESTION" />
                            </Button>
                        )}
                    />
                )}
            </Box>

            <QuestionEditorDialog
                open={questionDialogOpen}
                question={editingQuestion}
                idQuiz={idQuiz}
                idCourse={idCourse}
                onClose={() => { setQuestionDialogOpen(false); setEditingQuestion(null) }}
                onSaved={handleQuestionSaved}
            />

            <QuizImportDialog
                open={importOpen}
                idQuiz={idQuiz}
                idCourse={idCourse}
                onClose={() => setImportOpen(false)}
                onImported={() => { setImportOpen(false); loadQuestions() }}
            />

            <ConfirmDialog
                open={!!pendingDelete}
                danger
                title={<Langs str="VLEARN_EDITOR_DELETE_TITLE" />}
                message={<Langs str={pendingDelete && Number(pendingDelete.answered_count) > 0
                    ? 'VLEARN_EDITOR_DELETE_BODY_ANSWERED'
                    : 'VLEARN_EDITOR_DELETE_BODY'} />}
                confirmLabel={<Langs str="VLEARN_EDITOR_BTN_CONFIRM_DELETE" />}
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />

            {/* Deleting the quiz itself, as opposed to one of its questions. The server is the
                authority on whether this is allowed — it refuses when a lesson still uses the
                quiz or a learner has attempted it — so this dialog warns rather than decides,
                and the refusal reason is surfaced as a toast. */}
            <ConfirmDialog
                open={quizDeleteOpen}
                danger
                title={<Langs str="VLEARN_EDITOR_DELETE_QUIZ_TITLE" />}
                message={<Langs str="VLEARN_EDITOR_DELETE_QUIZ_BODY" />}
                confirmLabel={<Langs str="VLEARN_EDITOR_BTN_CONFIRM_DELETE" />}
                onConfirm={confirmDeleteQuiz}
                onCancel={() => setQuizDeleteOpen(false)}
            />
        </Box>
    )
}

// ── Pieces ──────────────────────────────────────────────────────────────────────────

function SettingSwitch({ checked, onChange, labelKey, helpKey }) {
    return (
        <Box>
            <FormControlLabel
                control={(
                    <Switch
                        checked={checked}
                        onChange={(event) => onChange(event.target.checked)}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--orange, rgb(255,144,21))' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'var(--orange, rgb(255,144,21))'
                            }
                        }}
                    />
                )}
                label={(
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary, #111318)' }}>
                        <Langs str={labelKey} />
                    </Typography>
                )}
                sx={{ mx: 0 }}
            />
            <Typography sx={{ ...vlearnType.meta, ml: 6, mt: '-4px' }}>
                <Langs str={helpKey} />
            </Typography>
        </Box>
    )
}

function questionTypeKey(type) {
    if (type === 'multiple') return 'VLEARN_QUESTION_TYPE_MULTIPLE'
    if (type === 'true_false') return 'VLEARN_QUESTION_TYPE_TRUE_FALSE'
    return 'VLEARN_QUESTION_TYPE_SINGLE'
}

const labelSx = {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-primary, #111318)',
    mb: 1,
    '&.Mui-focused': { color: 'var(--text-primary, #111318)' }
}

const helperSx = { ...vlearnType.meta, ml: 0, mt: 0.75 }

const primaryButtonSx = {
    borderRadius: '12px',
    px: 2.5,
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 650,
    fontSize: '0.9rem',
    backgroundColor: 'var(--orange, rgb(255,144,21))',
    color: '#fff',
    '&:hover': { backgroundColor: '#e07d0d' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' },
    '&.Mui-disabled': { backgroundColor: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.34)' }
}

const outlinedButtonSx = {
    borderRadius: '12px',
    px: 2.5,
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.88rem',
    color: 'var(--text-secondary, #4b5263)',
    borderColor: 'rgba(0,0,0,0.14)',
    '&:hover': { borderColor: 'rgba(0,0,0,0.28)', backgroundColor: 'rgba(0,0,0,0.03)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}

const quietButtonSx = {
    borderRadius: '12px',
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.88rem',
    color: 'var(--text-secondary, #4b5263)',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}

const iconButtonSx = {
    borderRadius: '9px',
    minWidth: 36,
    minHeight: 36,
    color: 'var(--text-muted, #8b92a0)',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' },
    '&.Mui-disabled': { color: 'rgba(0,0,0,0.18)' }
}
